import {
  ChangeDetectorRef,
  Component,
  ElementRef,
  EventEmitter,
  HostListener,
  Inject,
  NgZone,
  OnDestroy,
  Output,
  ViewEncapsulation,
  inject,
} from '@angular/core';
import { ClassItem } from '../../../interfaces/class.interface';
import { ClassService } from '../../../services/class.service';
import { UtilsService } from '../../../services/utils.service';
import { DIALOG_DATA, DialogService } from '../../../services/dialog.service';
import { retry, catchError } from 'rxjs/operators';
import { throwError } from 'rxjs';
import { CommonModule } from '@angular/common';
import { FormsModule, ReactiveFormsModule } from '@angular/forms';

@Component({
    selector: 'lib-booking-confirmation',
    templateUrl: './booking-confirmation.component.html',
    styleUrls: ['./booking-confirmation.component.css'],
    encapsulation: ViewEncapsulation.ShadowDom,
    standalone: true,
    imports: [CommonModule, FormsModule, ReactiveFormsModule]
})
export class BookingConfirmationComponent implements OnDestroy {
  isProcessing = false;
  isSuccess = false;
  hasError = false;
  errorMessage = '';
  successMessage = '';
  classDetails?: ClassItem;
  @Output() bookingSuccess = new EventEmitter<string>();

  private readonly cdr = inject(ChangeDetectorRef);
  private readonly ngZone = inject(NgZone);

  /** HttpClient withFetch() callbacks can run outside NgZone; refresh the view explicitly. */
  private notifyViewUpdate(): void {
    this.ngZone.run(() => this.cdr.detectChanges());
  }

  constructor(
    @Inject(DIALOG_DATA) public data: {
      classItem: ClassItem;
      tenantUuid: string;
      tenantId: number;
      isWaitlist: boolean;
      alreadyBooked?: boolean;
      theme?: Record<string, string>;
      androidAppLink?: string;
      iosAppLink?: string;
    },
    private classService: ClassService,
    private dialogService: DialogService,
    private elementRef: ElementRef,
    public utils: UtilsService
  ) {
    document.body.style.overflow = 'hidden';

    // Apply theme variables from dialog data if available
    if (data.theme) {
      Object.entries(data.theme).forEach(([key, value]) => {
        this.elementRef.nativeElement.style.setProperty(key, value);
      });
    }

    // Load class details
    this.loadClassDetails();

    // If already booked, show success message immediately
    if (data.alreadyBooked) {
      this.isSuccess = true;
      this.successMessage = 'You are already booked into this class';
      this.bookingSuccess.emit(data.classItem.uuid);
      this.notifyViewUpdate();
    }
  }

  private loadClassDetails() {
    this.classService.getClassById(this.data.tenantId, this.data.classItem.id)
      .subscribe({
        next: (details) => {
          this.classDetails = details;
          this.notifyViewUpdate();
        },
        error: (error) => {
          console.error('Error loading class details:', error);
          this.notifyViewUpdate();
        }
      });
  }

  onCancel() {
    this.closeDialog();
  }

  ngOnDestroy(): void {
    document.body.style.overflow = '';
  }

  private closeDialog(): void {
    document.body.style.overflow = '';
    this.dialogService.closeDialog();
  }

  onConfirm() {
    // If already booked, just close the dialog
    if (this.data.alreadyBooked) {
      this.onCancel();
      return;
    }

    this.isProcessing = true;
    this.hasError = false;
    this.notifyViewUpdate();

    this.classService.joinClass(
      this.data.tenantUuid,
      this.data.classItem.uuid,
      this.data.isWaitlist
    ).pipe(
      retry(3),
      catchError(error => {
        console.error('Error joining class:', error);
        return throwError(() => error);
      })
    ).subscribe({
      next: () => {
        this.isProcessing = false;
        this.isSuccess = true;
        this.successMessage = this.data.isWaitlist
          ? 'Added to waitlist'
          : 'Booking is now confirmed';
        this.bookingSuccess.emit(this.data.classItem.uuid);
        this.notifyViewUpdate();
      },
      error: (error) => {
        this.isProcessing = false;
        this.hasError = true;

        // Handle array of errors from the API
        if (Array.isArray(error)) {
          const firstError = error[0];
          this.errorMessage = firstError.errorMessage;
        } else if (error?.error && Array.isArray(error.error)) {
          const firstError = error.error[0];
          this.errorMessage = firstError.errorMessage;
        } else {
          this.errorMessage = 'Unable to complete booking. Please try again.';
        }
        this.notifyViewUpdate();
      }
    });
  }

  onOverlayClick(event: MouseEvent) {
    if (event.target === event.currentTarget) {
      this.onCancel();
    }
  }

  @HostListener('document:keydown.escape')
  onEscapePress() {
    this.onCancel();
  }

  addToGoogleCalendar() {
    const event = this.createCalendarEvent();
    const url = `https://www.google.com/calendar/render?action=TEMPLATE&text=${encodeURIComponent(event.title)}&dates=${event.start}/${event.end}&details=${encodeURIComponent(event.description)}&location=${encodeURIComponent(event.location)}`;
    window.open(url, '_blank');
  }

  addToAppleCalendar() {
    const event = this.createCalendarEvent();
    const url = `data:text/calendar;charset=utf8,${encodeURIComponent(this.createICSContent(event))}`;
    const link = document.createElement('a');
    link.href = url;
    link.download = 'class-booking.ics';
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
  }

  addToOutlookCalendar() {
    const event = this.createCalendarEvent();
    const url = `data:text/calendar;charset=utf8,${encodeURIComponent(this.createICSContent(event))}`;
    const link = document.createElement('a');
    link.href = url;
    link.download = 'class-booking.ics';
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
  }

  private createCalendarEvent() {
    const startDate = new Date(this.data.classItem.start_datetime);
    const endDate = new Date(startDate.getTime() + 60 * 60 * 1000); // Assuming 1 hour duration

    return {
      title: this.data.classItem.type_name_primary_language,
      description: `Class with ${this.data.classItem.coach_name}`,
      location: this.data.classItem.tenant_name_primary_language,
      start: this.formatDateForCalendar(startDate),
      end: this.formatDateForCalendar(endDate)
    };
  }

  private formatDateForCalendar(date: Date): string {
    return date.toISOString().replace(/[-:]/g, '').split('.')[0] + 'Z';
  }

  private createICSContent(event: any): string {
    return `BEGIN:VCALENDAR
VERSION:2.0
BEGIN:VEVENT
DTSTART:${event.start}
DTEND:${event.end}
SUMMARY:${event.title}
DESCRIPTION:${event.description}
LOCATION:${event.location}
END:VEVENT
END:VCALENDAR`;
  }

  // Helper method to check if any app links are available
  hasAppLinks(): boolean {
    return !!(this.data.androidAppLink || this.data.iosAppLink);
  }

  getCoachInitials(name: string): string {
    if (!name) return '';
    return name
      .split(' ')
      .map(word => word[0]?.toUpperCase())
      .slice(0, 2)
      .join('');
  }

  handleImageError(event: Event) {
    const imgElement = event.target as HTMLImageElement;
    if (imgElement?.parentElement) {
      imgElement.parentElement.style.display = 'none';
    }
  }
}